import type {
  AnalyticsTimeframe,
  AnalyticsUnit,
  ParamsGetMetabaseEmbed,
  ResGetMetabaseEmbed,
} from 'common/dist/types/apiTypes';
import { Page } from '@src/components/Page';
import { ADALONG_COLORS } from '@src/styles';
import { Pathname } from '@src/types/Browsing';
import { userAtom } from '@src/state/userAtom';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { classes, stylesheet } from 'typestyle';
import { useQueryMetabaseEmbed } from '@src/queries/statistics/useQueryMetabaseEmbed';
import { useQueryLatestUGCAgreedWidget } from '@src/queries/statistics/useQueryLatestUGCAgreedWidget';
import { useQuerySocialTopProducts } from '@src/queries/statistics/useQuerySocialTopProducts';
import { AnalyticsHeader, AnalyticsView } from './AnalyticsHeader';
import {
  ANALYTICS_TIMEFRAME_CONFIG,
} from './AnalyticsUnitTimeframePicker';
import { LegacyAnalyzePage } from './LegacyAnalyzePage';
import { MetabaseSdkDashboard } from './MetabaseSdkDashboard';
import { TopSocialProducts } from './TopSocialProducts';
import { LatestUGCAgreedWidget } from './LatestUGCAgreedWidget';

export const AnalyzePage = () => {
  const user = useAtomValue(userAtom);

  if (!user) {
    return (
      <Page path={Pathname.analyze}>
        <div className={sheet.initialState}>Loading analytics…</div>
      </Page>
    );
  }

  if (!user.groupFeatures.analyze_dashboard) {
    return <LegacyAnalyzePage />;
  }

  return <NewAnalyzePage />;
};

const NewAnalyzePage = () => {
  const [search, setSearch] = useState(() => window.location.search);
  const analyticsState = useMemo(() => readAnalyticsState(search), [search]);
  const { activeView, unit, timeframe } = analyticsState;
  const timeframeByUnit = useRef<Partial<Record<AnalyticsUnit, number>>>({});
  const numericTimeframe = typeof timeframe === 'number'
    ? timeframe
    : timeframeByUnit.current[unit] ?? ANALYTICS_TIMEFRAME_CONFIG[unit].defaultValue;

  useEffect(() => {
    const handlePopState = () => setSearch(window.location.search);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const canonicalSearch = writeAnalyticsState(search, analyticsState);
    if (canonicalSearch !== search) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${canonicalSearch}`);
      setSearch(canonicalSearch);
    }
  }, [analyticsState, search]);

  const updateAnalyticsState = (nextState: Partial<AnalyticsUrlState>) => {
    const nextSearch = writeAnalyticsState(search, { ...analyticsState, ...nextState });
    if (nextSearch !== search) {
      window.history.pushState(window.history.state, '', `${window.location.pathname}${nextSearch}`);
      setSearch(nextSearch);
    }
  };

  const selectUnit = (nextUnit: AnalyticsUnit) => {
    if (nextUnit === unit) {
      return;
    }

    if (typeof timeframe === 'number') {
      timeframeByUnit.current[unit] = timeframe;
    }
    const nextTimeframe = timeframeByUnit.current[nextUnit]
      ?? ANALYTICS_TIMEFRAME_CONFIG[nextUnit].defaultValue;
    updateAnalyticsState({ unit: nextUnit, timeframe: nextTimeframe });
  };

  const selectTimeframe = (nextTimeframe: AnalyticsTimeframe) => {
    if (typeof nextTimeframe === 'number') {
      timeframeByUnit.current[unit] = nextTimeframe;
    }
    updateAnalyticsState({ timeframe: nextTimeframe });
  };

  const ecommerceEmbedParams: ParamsGetMetabaseEmbed = {
    dashboard: 'ecom',
    unit,
    timeframe,
  };
  const ecommerceEmbed = useQueryMetabaseEmbed(ecommerceEmbedParams, activeView === 'ecommerce');
  // Dashboard 331 is configured under the existing `ugc_rights` key until
  // the four historical UGC dashboard configurations are retired.
  const ugcEmbedParams: ParamsGetMetabaseEmbed = {
    dashboard: 'ugc_rights',
    unit,
    timeframe,
  };
  const ugcEmbed = useQueryMetabaseEmbed(ugcEmbedParams, activeView === 'ugc');
  const activeEmbed = activeView === 'ecommerce' ? ecommerceEmbed : ugcEmbed;
  const activePeriods = activeEmbed.data?.periods;
  const activeEmbedIsLoading = isEmbedSelectionLoading(activeEmbed, unit, timeframe);
  const commercePeriods = ecommerceEmbed.data?.periods;
  const commercePeriodsMatch = !!commercePeriods
    && commercePeriods.unit === unit
    && commercePeriods.timeframe === timeframe;
  const topProducts = useQuerySocialTopProducts({
    ...(commercePeriods ? {
      from: commercePeriods.charts.start ?? '1970-01-01',
      to: commercePeriods.charts.end,
    } : {}),
    limit: 10,
  }, activeView === 'ecommerce' && commercePeriodsMatch && !activeEmbedIsLoading);
  const latestUGCWidget = useQueryLatestUGCAgreedWidget(activeView === 'ugc');

  return (
    <Page path={Pathname.analyze}>
      <div className={sheet.page}>
        <AnalyticsHeader
          activeView={activeView}
          unit={unit}
          timeframe={timeframe}
          numericTimeframe={numericTimeframe}
          onViewChange={(view) => updateAnalyticsState({ activeView: view })}
          onUnitChange={selectUnit}
          onTimeframeChange={selectTimeframe}
        />
        {activeView === 'ecommerce' ? (
          <main
            id="analytics-ecommerce-panel"
            role="tabpanel"
            aria-label="ecommerce analytics"
            className={sheet.dashboardContent}
            {...periodDebugAttributes(unit, timeframe, activePeriods)}
          >
            <div className={sheet.ecommerceDashboard}>
              <MetabaseSdkDashboard
                dashboardId={ecommerceEmbed.data?.dashboardId}
                token={ecommerceEmbed.data?.token}
                parameters={ecommerceEmbed.data?.parameters}
                selectionKey={`${unit}:${timeframe}`}
                title="E-commerce analytics dashboard"
                minHeight={640}
                isLoading={activeEmbedIsLoading}
                isError={ecommerceEmbed.isError}
                loadingMessage="Loading E-commerce dashboard…"
                errorMessage="Unable to load the E-commerce dashboard. Check the Metabase embed configuration."
                unavailableMessage="E-commerce dashboard coming soon."
              />
            </div>
            <TopSocialProducts
              data={topProducts.data}
              isLoading={!ecommerceEmbed.isError && (activeEmbedIsLoading || (commercePeriodsMatch && topProducts.isPending))}
              isError={ecommerceEmbed.isError || (!activeEmbedIsLoading && !commercePeriodsMatch) || topProducts.isError}
            />
          </main>
        ) : (
          <main
            id="analytics-ugc-panel"
            role="tabpanel"
            aria-label="ugc analytics"
            className={classes(sheet.dashboardContent, sheet.ugcDashboardContent)}
            {...periodDebugAttributes(unit, timeframe, activePeriods)}
          >
            <div className={sheet.ugcDashboard}>
              <MetabaseSdkDashboard
                dashboardId={ugcEmbed.data?.dashboardId}
                token={ugcEmbed.data?.token}
                parameters={ugcEmbed.data?.parameters}
                selectionKey={`${unit}:${timeframe}`}
                title="UGC analytics dashboard"
                minHeight={640}
                isLoading={activeEmbedIsLoading}
                isError={ugcEmbed.isError}
                loadingMessage="Loading UGC dashboard…"
                errorMessage="Unable to load the UGC dashboard. Check the Metabase embed configuration."
                unavailableMessage="UGC dashboard coming soon."
              />
            </div>
            {latestUGCWidget.data && !latestUGCWidget.isError && (
              <LatestUGCAgreedWidget
                token={latestUGCWidget.data.token}
              />
            )}
          </main>
        )}
      </div>
    </Page>
  );
};

type AnalyticsUrlState = {
  activeView: AnalyticsView;
  unit: AnalyticsUnit;
  timeframe: AnalyticsTimeframe;
};

const ANALYTICS_QUERY_KEYS = {
  view: 'view',
  unit: 'unit',
  timeframe: 'timeframe',
  preset: 'preset',
  periodMode: 'periodMode',
  start: 'start',
  end: 'end',
} as const;

const DEFAULT_ANALYTICS_UNIT: AnalyticsUnit = 'month';

function readAnalyticsState(search: string): AnalyticsUrlState {
  const params = new URLSearchParams(search);
  const activeView = params.get(ANALYTICS_QUERY_KEYS.view) === 'ugc' ? 'ugc' : 'ecommerce';
  const requestedUnit = params.get(ANALYTICS_QUERY_KEYS.unit);

  if (isAnalyticsUnit(requestedUnit)) {
    return {
      activeView,
      unit: requestedUnit,
      timeframe: normalizeTimeframe(params.get(ANALYTICS_QUERY_KEYS.timeframe), requestedUnit),
    };
  }

  return {
    activeView,
    ...migrateLegacyAnalyticsState(params),
  };
}

function writeAnalyticsState(search: string, state: AnalyticsUrlState): string {
  const params = new URLSearchParams(search);
  params.set(ANALYTICS_QUERY_KEYS.view, state.activeView);
  params.set(ANALYTICS_QUERY_KEYS.unit, state.unit);
  params.set(ANALYTICS_QUERY_KEYS.timeframe, String(state.timeframe));
  params.delete(ANALYTICS_QUERY_KEYS.preset);
  params.delete(ANALYTICS_QUERY_KEYS.periodMode);
  params.delete(ANALYTICS_QUERY_KEYS.start);
  params.delete(ANALYTICS_QUERY_KEYS.end);
  return `?${params.toString()}`;
}

function migrateLegacyAnalyticsState(params: URLSearchParams): Pick<AnalyticsUrlState, 'unit' | 'timeframe'> {
  const preset = params.get(ANALYTICS_QUERY_KEYS.preset);
  const presetMigration: Record<string, Pick<AnalyticsUrlState, 'unit' | 'timeframe'>> = {
    '7d': { unit: 'week', timeframe: 4 },
    '1m': { unit: 'week', timeframe: 4 },
    '3m': { unit: 'month', timeframe: 3 },
    '6m': { unit: 'month', timeframe: 6 },
    '12m': { unit: 'month', timeframe: 12 },
  };
  const migratedPreset = preset ? presetMigration[preset] : undefined;
  if (migratedPreset) {
    return migratedPreset;
  }

  const mode = params.get(ANALYTICS_QUERY_KEYS.periodMode);
  const unit: AnalyticsUnit = mode === 'day' || mode === 'week' ? 'week' : DEFAULT_ANALYTICS_UNIT;
  const start = parseLegacyDate(params.get(ANALYTICS_QUERY_KEYS.start));
  const end = parseLegacyDate(params.get(ANALYTICS_QUERY_KEYS.end));
  const approximateTimeframe = start && end
    ? unit === 'week'
      ? Math.max(1, Math.floor((end.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1)
      : (end.getUTCFullYear() - start.getUTCFullYear()) * 12
        + end.getUTCMonth() - start.getUTCMonth() + 1
    : ANALYTICS_TIMEFRAME_CONFIG[unit].defaultValue;

  return {
    unit,
    timeframe: normalizeTimeframe(String(approximateTimeframe), unit),
  };
}

function normalizeTimeframe(value: string | null, unit: AnalyticsUnit): AnalyticsTimeframe {
  if (value === 'all') {
    return 'all';
  }

  const config = ANALYTICS_TIMEFRAME_CONFIG[unit];
  const numericValue = Number(value);
  if (
    Number.isInteger(numericValue)
    && numericValue >= config.min
    && numericValue <= config.max
    && (numericValue - config.min) % config.step === 0
  ) {
    return numericValue;
  }

  return config.defaultValue;
}

function isAnalyticsUnit(value: string | null): value is AnalyticsUnit {
  return value === 'week' || value === 'month' || value === 'quarter' || value === 'year';
}

function parseLegacyDate(value: string | null): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
    ? date
    : null;
}

type MetabaseEmbedQuery = ReturnType<typeof useQueryMetabaseEmbed>;

function isEmbedSelectionLoading(
  embed: MetabaseEmbedQuery,
  unit: AnalyticsUnit,
  timeframe: AnalyticsTimeframe,
): boolean {
  const periods = embed.data?.periods;
  const hasMatchingPeriods = !embed.data || !periods || (periods.unit === unit && periods.timeframe === timeframe);
  // A background token refresh must not replace a valid dashboard with the
  // loading state. Only hide an already-resolved dashboard when its semantic
  // selection no longer matches the controls.
  return embed.isLoading || (!embed.isError && !hasMatchingPeriods);
}

function periodDebugAttributes(
  unit: AnalyticsUnit,
  timeframe: AnalyticsTimeframe,
  periods: ResGetMetabaseEmbed['periods'] | undefined,
): Record<string, string | undefined> {
  return {
    'data-period-unit': periods?.unit ?? unit,
    'data-period-timeframe': String(periods?.timeframe ?? timeframe),
    'data-kpi-period-label': periods?.kpi.label,
    'data-comparison-period-label': periods?.comparison?.label,
    'data-kpi-period-start': periods?.kpi.start,
    'data-kpi-period-end': periods?.kpi.end,
    'data-comparison-period-start': periods?.comparison?.start,
    'data-comparison-period-end': periods?.comparison?.end,
    'data-chart-period-start': periods?.charts.start ?? undefined,
    'data-chart-period-end': periods?.charts.end,
  };
}

const sheet = stylesheet({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  initialState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 320,
    color: ADALONG_COLORS.LIGHT_GRAY,
    fontSize: 14,
  },
  dashboardContent: {
    flex: 1,
    minHeight: 0,
    padding: 32,
    overflow: 'auto',
    $nest: {
      '&[hidden]': {
        display: 'none',
      },
      '@media (max-width: 980px)': {
        padding: 20,
      },
    },
  },
  ecommerceDashboard: {
    $nest: {
      // The SDK leaves 40px of empty space after its last card. Products render
      // immediately below this dashboard, so remove that trailing padding.
      '& [data-testid="dashboard-parameters-and-cards"]': {
        paddingBottom: '0 !important',
      },
    },
  },
  ugcDashboardContent: {
    display: 'block',
  },
  ugcDashboard: {
    width: '100%',
    maxWidth: 1600,
    margin: '0 auto',
    $nest: {
      '& [data-testid="dashboard-parameters-and-cards"]': {
        paddingBottom: '0 !important',
      },
      '@media (max-width: 980px)': {
        maxWidth: 'none',
      },
    },
  },
});
